import { z } from 'zod'

//---- Localized Error Messages Utility Class ----//

const paramsSchema = z.record(z.union([z.string(), z.number()]))

export const createMessageFromTemplate = (
  params: z.infer<typeof paramsSchema>,
  messageTemplate: string,
) => {
  return messageTemplate.replace(/{([^}]+)}/g, (match, paramName) => {
    if (params !== undefined && params[paramName] !== undefined) {
      return params[paramName]?.toString() ?? '' //TODO: confirm this logic
    } else {
      return match
    }
  })
}

//----- Localized Messages -----//

export enum LocalizedErrorKey {
  UNKNOWN = 'UNKNOWN', // Catch-all for errors that app wants to report, but no specific key exists.
  UNKNOWN_FALLBACK = 'UNKNOWN_FALLBACK', // Catch-all for errors that don't have a specific key on decode.
  UNAUTHORIZED = 'UNAUTHORIZED',

  NOT_OWNER_OF_BUNDLE = 'NOT_OWNER_OF_BUNDLE',
  BUNDLE_NOT_FOUND = 'BUNDLE_NOT_FOUND',
  RECONSTRUCTION_FAILED = 'RECONSTRUCTION_FAILED',

  // Quota Errors
  OVER_QUOTA_OBJECT_SCAN = 'OVER_QUOTA_OBJECT_SCAN',
  OVER_QUOTA_MONOCLE_VIDEO = 'OVER_QUOTA_MONOCLE_VIDEO',
  OVER_QUOTA_PRIME_JOB = 'OVER_QUOTA_PRIME_JOB',
  OVER_QUOTA_STORAGE = 'OVER_QUOTA_STORAGE',

  // Monocle Prime Errors
  FAILED_TO_CREATE_PAYLOAD = 'FAILED_TO_CREATE_PAYLOAD',
  FAILED_TO_CREATE_BUNDLE = 'FAILED_TO_CREATE_BUNDLE',
  FAILED_TO_REVERT_BUNDLE = 'FAILED_TO_REVERT_BUNDLE',
  FAILED_TO_UPLOAD_POSTER = 'FAILED_TO_UPLOAD_POSTER',
  FAILED_TO_RELOAD_TOKENID = 'FAILED_TO_RELOAD_TOKENID',
  FAILED_TO_RESEND_VERIFICATION_EMAIL = 'FAILED_TO_RESEND_VERIFICATION_EMAIL',
  FAILED_TO_DELETE_ACCOUNT = 'FAILED_TO_DELETE_ACCOUNT',
}

const errorMessagesByLanguage = z.record(z.string(), z.string())

export const LocalizedErrorMessageTemplate = z.object({
  code: z.number(),
  expects: z.record(z.string(), z.any()),
  messages: errorMessagesByLanguage,
})

const LocalizedErrorMessageTemplates = z.record(
  z.nativeEnum(LocalizedErrorKey).default(LocalizedErrorKey.UNKNOWN_FALLBACK),
  LocalizedErrorMessageTemplate,
)

type LocalizedErrorMessageTemplate = z.infer<
  typeof LocalizedErrorMessageTemplate
>

type LocalizedErrorMessages = z.infer<typeof LocalizedErrorMessageTemplates>

export const localizedErrorMessageTemplates: LocalizedErrorMessages =
  LocalizedErrorMessageTemplates.parse({
    UNKNOWN: {
      code: 500,
      expects: { details: 'Example details that goes here' },
      messages: {
        en: 'Something went wrong: {details}',
        jp: '何かがうまくいかなかった: {details}',
      },
    },

    UNKNOWN_FALLBACK: {
      code: 500,
      expects: {},
      messages: {
        en: 'Something went wrong, please contact support',
        jp: '何かがうまくいかなかったので、サポートに連絡してください',
      },
    },

    UNAUTHORIZED: {
      code: 401,
      expects: {},
      messages: { en: 'Unauthorized', jp: '認証されていません' },
    },

    NOT_OWNER_OF_BUNDLE: {
      code: 400,
      expects: { user_id: '5555-5555', bundle_id: '1234-1234' },
      messages: {
        en: 'User {user_id} is not the owner of bundle {bundle_id}',
        jp: 'ユーザー {user_id} はバンドル {bundle_id} のオーナーではありません',
      },
    },
    BUNDLE_NOT_FOUND: {
      code: 404,
      expects: { user_id: '5555-5555', bundle_id: '1234-1234' },
      messages: {
        en: 'Bundle {bundle_id} not found for user {user_id}',
        jp: 'ユーザー {user_id} のバンドル {bundle_id} が見つかりません',
      },
    },
    RECONSTRUCTION_FAILED: {
      code: 403,
      expects: {},
      messages: { en: 'Reconstruction Failed', jp: '再構築に失敗しました' },
    },

    // Quota Errors
    OVER_QUOTA_OBJECT_SCAN: {
      code: 403,
      expects: { quota: 50 },
      messages: {
        en: 'Created object scan would be over quota (limit: {quota})',
        jp: 'オブジェクトスキャンがクオータを超えてしまいます (上限: {quota})',
      },
    },
    OVER_QUOTA_MONOCLE_VIDEO: {
      code: 403,
      expects: { quota: 20 },
      messages: {
        en: 'Created object scan would be over quota (limit: {quota})',
        jp: 'オブジェクトスキャンがクオータを超えてしまいます (上限: {quota})',
      },
    },
    OVER_QUOTA_PRIME_JOB: {
      code: 403,
      expects: { quota: 5 },
      messages: {
        en: 'Created prime job would be over quota (limit: {quota})',
        jp: 'プライムジョブがクオータを超えてしまいます (上限: {quota})',
      },
    },
    OVER_QUOTA_STORAGE: {
      code: 403,
      expects: { quota: 10000000 },
      messages: {
        en: 'User Storage is Over Quota (limit: {quota})',
        jp: 'ユーザーストレージがクオータを超えてしまいます (上限: {quota})',
      },
    },

    // Monocle Prime Errors
    FAILED_TO_CREATE_PAYLOAD: {
      code: 500,
      expects: {},
      messages: {
        en: 'Failed to Create Payload',
        jp: 'ペイロードの作成に失敗しました',
      },
    },
    FAILED_TO_CREATE_BUNDLE: {
      code: 500,
      expects: {},
      messages: {
        en: 'Failed to Create Bundle',
        jp: 'バンドルの作成に失敗しました',
      },
    },
    FAILED_TO_REVERT_BUNDLE: {
      code: 500,
      expects: {},
      messages: {
        en: 'Failed to Revert Bundle',
        jp: 'バンドルのリバートに失敗しました',
      },
    },
    FAILED_TO_UPLOAD_POSTER: {
      code: 500,
      expects: {},
      messages: {
        en: 'Failed to Upload Poster',
        jp: 'ポスターのアップロードに失敗しました',
      },
    },
    FAILED_TO_RELOAD_TOKENID: {
      code: 500,
      expects: {},
      messages: {
        en: 'Failed to Reload TokenID',
        jp: 'トークンIDのリロードに失敗しました',
      },
    },
    FAILED_TO_RESEND_VERIFICATION_EMAIL: {
      code: 500,
      expects: {},
      messages: {
        en: 'Failed to Resend Verification Email',
        jp: '確認メールの再送信に失敗しました',
      },
    },
    FAILED_TO_DELETE_ACCOUNT: {
      code: 500,
      expects: {},
      messages: {
        en: 'Failed to Delete Account',
        jp: 'アカウントの削除に失敗しました',
      },
    },
  })

//----- Localized Error DTO -----//
// Example:
// {
//   "errorKey": "OVER_QUOTA_OBJECT_SCAN",
//   "params": {
//     "quota": 50
//   }
// }
export const LocalizedErrorDto = z.object({
  error_key: z.nativeEnum(LocalizedErrorKey),
  params: paramsSchema,
})

export function getLocalizedMessage(
  errorObject: LocalizedError,
  language: string,
): string {
  const error = localizedErrorMessageTemplates[errorObject.error_key]
  const messageTemplate =
    error?.messages[language] || error?.messages['en'] || ''
  const messageParams = errorObject.params ?? []
  return createMessageFromTemplate(messageParams, messageTemplate)
}

export const localizedErrorSchema = z.object({
  error_key: z.nativeEnum(LocalizedErrorKey),
  params: paramsSchema,
  stack: z.string().optional(),
})

export type LocalizedError = z.infer<typeof localizedErrorSchema>
